import cookies from "js-cookie";

/**
 * Mise en forme du catalogue pour la page d'accueil.
 *
 * `GET /customer/products` renvoie une liste plate de produits, chacun portant
 * sa catégorie et sa sur-catégorie. Le design « Catalogue C » veut l'inverse :
 * un rail de sur-catégories, des pastilles de catégories, puis les produits.
 *
 * Trois icônes distinctes, toutes des URL servies par l'API :
 *   produit        → `product.icon`
 *   catégorie      → `category.dark_icon` au repos, `category.icon` actif
 *   sur-catégorie  → `over_category.icon`
 */

/** Image de repli, déjà utilisée par les cartes produit actuelles. */
export const FALLBACK_PRODUCT_ICON = "/assets/imgs/box_img.png";

/*
 * Même repli que src/i18n.js : sans cookie (ou avec une valeur inconnue),
 * l'interface est en anglais — le catalogue doit l'être aussi.
 */
export const localeKey = () => {
  const cookie = cookies.get("i18next");

  if (cookie === "fn") {
    return "fr";
  }

  return cookie === "du" ? "nl" : "en";
};

/*
 * Attention : produits et catégories ne rangent pas les langues pareil.
 * Relevé sur l'API réelle par le mobile (use-catalog.ts) :
 *
 *   produit                   name (en) · name_fr (fr) · name_nl
 *   catégorie, sur-catégorie  name (en) · name_en (fr !) · name_nl
 *
 * La vue products expose en plus `name_fr` pour les catégories, rempli avec
 * la colonne name_en : on le préfère quand il est là, ce qui reste juste le
 * jour où le backend corrigera ses colonnes.
 *
 * La description de catégorie suit la convention produit : `description`
 * pour l'anglais.
 */

const clean = (value) => (value && String(value).trim() ? String(value).trim() : undefined);

/** Nom d'une catégorie ou sur-catégorie (localizedCategoryName du mobile). */
const groupName = (group, locale) => {
  if (!group) return "";
  const en = clean(group.name);
  const fr = clean(group.name_fr) ?? clean(group.name_en);
  const nl = clean(group.name_nl);
  if (locale === "en") return en ?? fr ?? nl ?? "";
  if (locale === "nl") return nl ?? fr ?? en ?? "";
  return fr ?? en ?? nl ?? "";
};

export const productName = (product, locale = localeKey()) => {
  if (locale === "fr") {
    return product?.name_fr || product?.name || "";
  }

  return locale === "nl"
    ? product?.name_nl || product?.name || ""
    : product?.name || "";
};

export const productDescription = (product, locale = localeKey()) => {
  if (locale === "fr") {
    return product?.description_fr || product?.description || "";
  }

  return locale === "nl"
    ? product?.description_nl || product?.description || ""
    : product?.description || "";
};

export const categoryName = (category, locale = localeKey()) => groupName(category, locale);

export const categoryDescription = (category, locale = localeKey()) => {
  if (locale === "fr") {
    return category?.description_fr || category?.description || "";
  }

  return locale === "nl"
    ? category?.description_nl || category?.description || ""
    : category?.description || "";
};

export const overCategoryName = (over, locale = localeKey()) => groupName(over, locale);

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

/**
 * Regroupe les produits actifs en sur-catégories → catégories → produits,
 * chaque niveau trié par `order_by`. Un niveau sans produit n'apparaît pas.
 *
 * @returns {Array} [{ key, name, icon, order, categories: [{ key, name, icon,
 *                    darkIcon, order, products: [...] }] }]
 */
export const buildCatalogue = (products, locale = localeKey()) => {
  const overs = new Map();

  (products || [])
    .filter((product) => product?.is_active && product?.category?.over_category)
    .forEach((product) => {
      const category = product.category;
      const over = category.over_category;
      const overKey = String(over.id ?? over.name ?? "");
      const categoryKey = String(category.id ?? category.name ?? "");

      if (!overs.has(overKey)) {
        overs.set(overKey, {
          key: overKey,
          name: overCategoryName(over, locale),
          /* Nom anglais conservé tel quel : c'est lui qui identifie une
             sur-catégorie dans le code (le kilo, par exemple), pas le
             libellé affiché, qui change avec la langue. */
          englishName: over.name || "",
          icon: over.icon || null,
          order: over.order_by,
          categories: new Map(),
        });
      }

      const entry = overs.get(overKey);

      if (!entry.categories.has(categoryKey)) {
        entry.categories.set(categoryKey, {
          key: categoryKey,
          name: categoryName(category, locale),
          description: categoryDescription(category, locale),
          /*
           * Deux variantes, nommées d'après leur couleur et non d'après
           * l'état : `dark_icon` est le picto sombre, pour fond clair (onglet
           * au repos) ; `icon` est le picto blanc, pour fond indigo (onglet
           * actif). Voir l'ancienne page, user.css `.icons img.first/.second`.
           */
          icon: category.icon || null,
          darkIcon: category.dark_icon || null,
          order: category.order_by,
          products: [],
        });
      }

      entry.categories.get(categoryKey).products.push({
        id: product.id,
        name: productName(product, locale),
        description: productDescription(product, locale),
        icon: product.icon || FALLBACK_PRODUCT_ICON,
        price: Number(product.price) || 0,
        raw: product,
      });
    });

  return [...overs.values()]
    .map((over) => ({
      ...over,
      categories: [...over.categories.values()].sort(byOrder),
    }))
    .filter((over) => over.categories.length)
    .sort(byOrder);
};

/*
 * Les sur-catégories facturées au poids, qui affichent l'encart
 * d'estimation : l'ancienne « Per kilo » et « Everyday », qui porte le linge
 * au kilo sur staging. Comparées sur le nom anglais, stable d'une langue à
 * l'autre.
 */
const KILO_OVER_CATEGORIES = ["per kilo", "everyday"];

export const isKiloOverCategory = (over) =>
  KILO_OVER_CATEGORIES.includes(
    String(over?.englishName || "").trim().toLowerCase()
  );

/** Prix formaté comme le design : « 19,74 € ». */
export const formatPrice = (value) =>
  `${(Number(value) || 0).toFixed(2).replace(".", ",")} €`;
